// src/views/OrganizationsSubview.tsx
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import CoursesApi from '../../../../apis/CoursesApi';
import OrganizationsApi from '../../../../apis/OrganizationsApi';
import ManagementView from '../../../../components/ManagementGrid/ManagementView';
import UnexpectedError from '../../../../components/UnexpectedError/UnexpectedError';
import { OrganizationDto } from '../../../../generated-types/organizationDto';
import { OrgSubscriptionStatus } from '../../../../generated-types/orgSubscriptionStatus';
import useApi from '../../../../lib/useApi';
import DataControl, {
  ModifiedDataGridProps,
} from '../../Components/DataControl';
import OrganizationsGridToolbar from './components/OrganizationsGridToolbar';
import ProgressBar from './components/ProgressBar';

/**
 * The OrganizationsSubview component renders the organizations table.
 */
const OrganizationsSubview = () => {
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [processedRows, setProcessedRows] = useState<any[]>([]);
  const [courseHeaders, setCourseHeaders] = useState<Record<string, string>>(
    {}
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [viewOrg, setViewOrg] = useState<{ id: string; name: string } | null>(
    null
  );

  // Apis
  const orgApi = useApi(OrganizationsApi);
  const coursesApi = useApi(CoursesApi);

  /**
   * Fetch organizations from the Organizations API.
   */
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Using GetAll (stubbed) from your Organizations API.
      const data = await orgApi.GetAll();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orgApi]);

  /**
   * Fetch the course ID-to-title mapping from the management API.
   */
  useEffect(() => {
    coursesApi
      .GetIdNameMapping()
      .then(setCourseHeaders)
      .catch((err) => {
        console.error('Error fetching course mapping:', err);
      });
  }, [coursesApi]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * Process raw organization data into rows for the grid.
   * For each organization, loop through its courseProgress array
   * and add a property for each course. Multiply progressPercent by 100.
   */
  useEffect(() => {
    if (organizations.length === 0) {
      setProcessedRows([]);
      return;
    }
    const newRows = organizations.map((org) => {
      const processedOrg = { ...org } as any;
      if (org.courseProgress && org.courseProgress.length > 0) {
        org.courseProgress.forEach((progress) => {
          if (courseHeaders[progress.courseId]) {
            processedOrg[progress.courseId] = progress.progressPercent;
          }
        });
      }
      return processedOrg;
    });
    setProcessedRows(newRows);
  }, [organizations, courseHeaders]);

  // Define the static base columns.
  const baseColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.6,
    },
    {
      field: 'licenses',
      headerName: 'Licenses',
      flex: 0.5,
      type: 'number',
    },
    {
      field: 'members',
      headerName: 'Members',
      flex: 0.5,
      type: 'number',
    },
  ];

  // Define dynamic course columns using the courseHeaders mapping.
  const courseColumns: GridColDef[] = Object.keys(courseHeaders).map(
    (courseId) => ({
      field: courseId,
      headerName: courseHeaders[courseId],
      flex: 0.75,
      type: 'number',
      valueGetter: (params) => params.row[courseId] ?? 0,
      valueFormatter: (params) => `${Math.round(params.value)}%`,
      renderCell: (params) => {
        return <ProgressBar value={params.value} />;
      },
    })
  );

  // Renewal Date column with special rendering.
  const renewalColumn: GridColDef = {
    field: 'subscriptionStatus',
    headerName: 'End Of Period',
    flex: 0.6,
    valueGetter: (params) => params.row.subscriptionStatus.endOfPeriod,
    renderCell: (params) => {
      const subStatus = params.row.subscriptionStatus as OrgSubscriptionStatus;
      const dateTypography = (
        <Typography>{subStatus.endOfPeriod.toLocaleDateString()}</Typography>
      );

      // If we're set to renew and it's active, just show the date
      if (subStatus.willRenew && subStatus.active) {
        return (
          <Box width="100%" display="flex" justifyContent="center">
            {dateTypography}
          </Box>
        );
      }

      // Set the icon
      let icon: JSX.Element;
      if (!subStatus.active || subStatus.endOfPeriod < new Date()) {
        icon = <ErrorRoundedIcon color="error" sx={{ cursor: 'help' }} />;
      } else {
        icon = (
          <WarningAmberRoundedIcon color="warning" sx={{ cursor: 'help' }} />
        );
      }

      // Set the tooltip
      let tooltip: string;
      if (!subStatus.active) {
        tooltip = 'Inactive subscription';
      } else if (subStatus.endOfPeriod < new Date()) {
        tooltip = 'Subscription expired';
      } else {
        tooltip = 'Not set to renew';
      }

      return (
        <Tooltip title={tooltip} arrow>
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap=".5em"
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="70px"
            >
              {dateTypography}
            </Box>
            {icon}
          </Box>
        </Tooltip>
      );
    },
  };

  // Actions column to allow archiving.
  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const subStatus = params.row.subscriptionStatus as OrgSubscriptionStatus;
      const canArchive = !subStatus.active;
      return (
        <Tooltip
          title={
            canArchive
              ? 'Archive Organization'
              : 'Cannot archive active organization'
          }
          arrow
        >
          <span style={{ cursor: canArchive ? 'pointer' : 'not-allowed' }}>
            <IconButton
              color="error"
              disabled={!canArchive}
              onClick={(e) => {
                e.stopPropagation();
                orgApi.Archive(params.row.id).then(() => {
                  fetchOrganizations();
                });
              }}
            >
              <ArchiveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
  };

  // Combine all columns: base, dynamic course columns, renewal date, and actions.
  const columns: GridColDef[] = [
    ...baseColumns,
    ...courseColumns,
    renewalColumn,
    actionsColumn,
  ];

  // Setup additional DataGrid props consistent with your management grid.
  const dataGridProps: ModifiedDataGridProps = {
    components: {
      Toolbar: OrganizationsGridToolbar,
    },
    componentsProps: {
      toolbar: {
        onRefreshClick: fetchOrganizations,
      },
    },
    loading: loading,
    onRowClick: (params: any) => setViewOrg(params.row),
    getRowId: (row: OrganizationDto) => row.id,
    sx: {
      width: '100%',
      minWidth: '1000px',
      '& .MuiDataGrid-row:hover': {
        cursor: 'pointer',
      },
    },
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      {error ? (
        <UnexpectedError errorContext="the organizations" />
      ) : (
        <DataControl
          pageTitle="Organizations"
          gridColumns={columns}
          rowData={processedRows}
          dataGridProps={dataGridProps}
          viewContent={
            viewOrg != null ? <ManagementView orgId={viewOrg.id} /> : <></>
          }
          viewShowing={viewOrg != null}
          viewTitle={`Viewing - ${viewOrg?.name}`}
          hideView={() => setViewOrg(null)}
        />
      )}
    </Box>
  );
};

export default OrganizationsSubview;
